import type { TypedDocumentNode } from "@graphql-typed-document-node/core"
import { print } from "graphql"

type GraphQLErrorEntry = {
  message: string
  path?: ReadonlyArray<string | number>
  extensions?: { code?: string }
}

type GraphQLResponse<T> = { data: T; errors?: GraphQLErrorEntry[] }

function extractFiles(obj: unknown, path = "variables") {
  const files = new Map<number, { file: File; path: string }>()
  let i = 0

  function walk(value: unknown, currentPath: string): unknown {
    if (value instanceof File) {
      files.set(i++, { file: value, path: currentPath })
      return null
    }
    if (Array.isArray(value)) {
      return value.map((item, idx) => walk(item, `${currentPath}.${idx}`))
    }
    if (value && typeof value === "object") {
      const clone: Record<string, unknown> = {}
      for (const [key, val] of Object.entries(value)) {
        clone[key] = walk(val, `${currentPath}.${key}`)
      }
      return clone
    }
    return value
  }

  const clone = walk(obj, path)
  return { clone, files }
}

function toMultipartBody(
  query: string,
  { clone, files }: ReturnType<typeof extractFiles>,
) {
  const formData = new FormData()
  formData.append("operations", JSON.stringify({ query, variables: clone }))

  const map: Record<string, string[]> = {}
  files.forEach(({ file, path }, idx) => {
    const key = String(idx)
    map[key] = [path]
    formData.append(key, file)
  })
  formData.append("map", JSON.stringify(map))
  return formData
}

export function createGraphQLClient(url: string) {
  async function request<T, V extends Record<string, unknown> = Record<string, unknown>>(
    document: TypedDocumentNode<T, V>,
    variables?: V,
  ): Promise<T> {
    const query = print(document)
    const extracted = extractFiles(variables ?? {})

    let response: Response

    if (extracted.files.size === 0) {
      response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query, variables }),
      })
    } else {
      response = await fetch(url, {
        method: "POST",
        credentials: "include",
        body: toMultipartBody(query, extracted),
      })
    }

    if (!response.ok) {
      throw new Error(`GraphQL request failed: ${response.status}`)
    }

    const res = (await response.json()) as GraphQLResponse<T>

    if (res.errors?.length) {
      throw new GraphQLClientError({ errors: res.errors })
    }

    return res.data
  }

  // fetch reports no upload progress, so file uploads that show a bar go through XHR
  function upload<T, V extends Record<string, unknown> = Record<string, unknown>>(
    document: TypedDocumentNode<T, V>,
    variables: V,
    { onProgress }: { onProgress: (fraction: number) => void },
  ): Promise<T> {
    const body = toMultipartBody(print(document), extractFiles(variables))

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open("POST", url)
      xhr.withCredentials = true
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress(event.loaded / event.total)
      }
      xhr.upload.onload = () => onProgress(1)
      xhr.onload = () => {
        if (xhr.status < 200 || xhr.status >= 300) {
          reject(new Error(`GraphQL request failed: ${xhr.status}`))
          return
        }
        try {
          const res = JSON.parse(xhr.responseText) as GraphQLResponse<T>
          if (res.errors?.length) reject(new GraphQLClientError({ errors: res.errors }))
          else resolve(res.data)
        } catch (error) {
          reject(error instanceof Error ? error : new Error("Invalid GraphQL response"))
        }
      }
      xhr.onerror = () => reject(new Error("Network error"))
      xhr.send(body)
    })
  }

  function subscribe<TData, TVariables>(
    document: TypedDocumentNode<TData, TVariables>,
    variables?: TVariables,
  ) {
    const query = print(document)
    const sseUrl = new URL(url)
    sseUrl.searchParams.append("query", query)

    if (variables) {
      sseUrl.searchParams.append("variables", JSON.stringify(variables))
    }

    let eventSource: EventSource | null = null
    let onEventCallback: (data: TData) => void = () => {}

    const listener = (event: MessageEvent) => {
      const parsed = JSON.parse(event.data) as { data: TData }
      onEventCallback(parsed.data)
    }

    const connect = () => {
      if (eventSource) {
        eventSource.removeEventListener("next", listener)
        eventSource.close()
      }
      eventSource = new EventSource(sseUrl, { withCredentials: true })
      eventSource.addEventListener("next", listener)
    }

    connect()

    return {
      close: () => {
        eventSource?.removeEventListener("next", listener)
        eventSource?.close()
        eventSource = null
      },
      connect,
      onEvent: (cb: (data: TData) => void) => {
        onEventCallback = cb
      },
    }
  }

  return { request, subscribe, upload }
}

export const client = createGraphQLClient(`${window.location.origin}/api/graphql`)

export class GraphQLClientError extends Error {
  readonly response: { errors: GraphQLErrorEntry[] }

  constructor(response: { errors: GraphQLErrorEntry[] }) {
    super(response.errors[0]?.message ?? "GraphQL request failed")
    this.name = "GraphQLClientError"
    this.response = response
  }
}
